import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Package, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get('as') || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Left Side - Wellness Imagery */}
      <div className="auth-image-side" style={{background: 'linear-gradient(135deg, #2D3E2F 0%, #1E2B20 100%)'}}>
        <div className="relative w-full h-full overflow-hidden">
          {/* Background collage of flowers and herbs */}
          <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-2 p-4">
            <img 
              src="https://images.unsplash.com/photo-1644409496856-a92543edbc64?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
              alt="Lavender flowers"
              className="w-full h-full object-cover rounded-lg"
            />
            <img 
              src="https://images.unsplash.com/photo-1686298804691-c7df01a856ef?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
              alt="Chamomile field"
              className="w-full h-full object-cover rounded-lg"
            />
            <img 
              src="https://images.unsplash.com/photo-1767479740512-3aad65a3f92b?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
              alt="White daisies"
              className="w-full h-full object-cover rounded-lg"
            />
            <img 
              src="https://images.unsplash.com/photo-1619545662764-9006c774e613?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
              alt="Purple lavender"
              className="w-full h-full object-cover rounded-lg"
            />
          </div>
          
          {/* Overlay content */}
          <div className="relative z-10 flex flex-col items-center justify-center h-full px-12 text-center">
            <div className="bg-[#2D3E2F]/80 backdrop-blur-sm rounded-2xl p-8 shadow-2xl">
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-[#7CB342] rounded-xl flex items-center justify-center shadow-lg">
                  <Package className="w-10 h-10 text-white" />
                </div>
              </div>
              <h1 className="text-4xl font-bold text-white tracking-tight mb-3" style={{fontFamily: 'Work Sans, sans-serif'}}>
                SupplyTrack
              </h1>
              <p className="text-[#A5D6A7] text-lg">
                POS, Inventory & Store Operations for health retailers
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="auth-form-side bg-[#FAFDF7]">
        <Card className="w-full max-w-md border-0 shadow-none bg-transparent">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center mb-4 lg:hidden">
              <div className="w-12 h-12 bg-[#2D3E2F] rounded-xl flex items-center justify-center">
                <Package className="w-7 h-7 text-white" />
              </div>
            </div>
            <CardTitle className="text-3xl font-bold tracking-tight text-[#1B5E20]" style={{fontFamily: 'Work Sans, sans-serif'}}>
              Welcome Back
            </CardTitle>
            <CardDescription className="text-[#558B2F] mt-2">
              Sign in to manage your wellness inventory
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-[#2D3E2F]">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="h-11 rounded-lg border-[#C8E6C9] focus:border-[#2D3E2F] focus:ring-[#2D3E2F] bg-white"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-[#2D3E2F]">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="h-11 rounded-lg border-[#C8E6C9] focus:border-[#2D3E2F] focus:ring-[#2D3E2F] bg-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#558B2F] hover:text-[#2D3E2F]"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              {/* Forgot Password Link */}
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setShowResetPassword(true)}
                  className="text-sm text-[#558B2F] hover:text-[#2D3E2F] font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
              
              <Button 
                type="submit" 
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full h-11 bg-[#2D3E2F] hover:bg-[#1E2B20] text-white font-semibold tracking-wide rounded-lg"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 spinner" /> Signing In...</>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-[#558B2F] text-sm" data-testid="invite-only-note">
                Logins are set up by SupplyTrack for each business — ask your provider if you need one.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Reset Password — handled by the provider (platform admin) */}
      {showResetPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowResetPassword(false)}>
          <Card className="w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()} data-testid="reset-password-info">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-[#2D3E2F]">Forgot your password?</CardTitle>
              <CardDescription>
                For security, passwords are reset by your SupplyTrack provider. Send them a message and they will set a new one for you in a minute.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button type="button" onClick={() => setShowResetPassword(false)} className="w-full bg-[#2D3E2F] hover:bg-[#1E2B20]" data-testid="reset-password-close">Got it</Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Login;
